import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export type FeatureAutomations = {
    isLoading: boolean;
    accessLevel: AutomationsAccessLevel;
    capabilities: Set<AutomationsCapabilities>;
};

export enum AutomationsAccessLevel {
    None = "none",
    Teaser = "teaser",
    Full = "full",
}

export enum AutomationsCapabilities {
    PullRequestTrigger = "pull-request-trigger",
}

export const useFeatureAutomations = (): FeatureAutomations => {
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.WorkflowsEnabled, false);
    const { value: prTriggerAllowed, loading: prTriggerLoading } = useFlag(
        Flags.AutomationsPullRequestTriggerAllowed,
        false,
    );

    if (isPendingOrganization || flagLoading || prTriggerLoading) {
        return {
            accessLevel: AutomationsAccessLevel.None,
            capabilities: new Set(),
            isLoading: true,
        };
    }

    // Access controlled by organization tier and organization configuration
    let feature: FeatureAutomations = {
        accessLevel: AutomationsAccessLevel.None,
        capabilities: new Set(),
        isLoading: false,
    };
    switch (organization?.tier) {
        case OrganizationTier.FREE: {
            feature = {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
        case OrganizationTier.FREE_ONA: {
            feature = {
                accessLevel: AutomationsAccessLevel.Teaser,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
        case OrganizationTier.CORE: {
            feature = {
                accessLevel: AutomationsAccessLevel.Full,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
        case OrganizationTier.ENTERPRISE: {
            // TODO(Enterprise rollout): Introduce logic for when to have it enabled by default for enterprise customers
            feature = {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set([AutomationsCapabilities.PullRequestTrigger]),
                isLoading: false,
            };
            break;
        }
        default: {
            feature = {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set(),
                isLoading: false,
            };
            break;
        }
    }

    // Apply feature flag overwrites
    if (flagEnabled) {
        feature.accessLevel = AutomationsAccessLevel.Full;
    }
    if (prTriggerAllowed) {
        feature.capabilities.add(AutomationsCapabilities.PullRequestTrigger);
    }

    return feature;
};
